import { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import {
  CalendarDays, Users, UserMinus, AlertTriangle, GripVertical,
  Loader2, Info, Copy, Printer, ShieldAlert,
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

// Drag-and-drop payload key — we send the volunteer id (when dragging
// from the repairer pool) or an assignment id (when dragging a chip
// between slots).
const DRAG_KIND_USER = 'user';
const DRAG_KIND_ASSIGNMENT = 'assignment';

/**
 * Phase 8 Sprint 3 — Super Admin rota drag-and-drop UI.
 *
 * Left rail: list of active Repairer volunteers (the only role allowed
 * on the rota; Front of House / Unavailable are filtered out server-
 * side too).
 *
 * Right: a matrix of slots × assignments for the currently-selected
 * event. Drag a repairer card from the rail onto a slot to assign;
 * drag an existing chip between slots to move it; click the ✕ on a
 * chip to remove.
 *
 * Capacity reminder shown when `coverage.needs_roster` is true.
 */
export default function ScheduleManager() {
  const { getAuthHeader, user } = useAuth();
  const [events, setEvents] = useState([]);
  const [selectedSortDate, setSelectedSortDate] = useState('');
  const [repairers, setRepairers] = useState([]);
  const [timeSlots, setTimeSlots] = useState([]);
  const [coverage, setCoverage] = useState(null);
  // At-risk bookings: array of {slot, category_name, active_bookings, capacity, over_by, kind}.
  // Populated alongside coverage so the warning panel updates after every
  // rota mutation (assign, move, remove, copy).
  const [atRisk, setAtRisk] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  // Local drag-over indicator for visual feedback per slot
  const [dragOverSlot, setDragOverSlot] = useState(null);
  // "Copy last event's rota" in-flight indicator
  const [copying, setCopying] = useState(false);
  // "Print rota as PDF" in-flight indicator
  const [printing, setPrinting] = useState(false);

  // ─── Bootstrap ─────────────────────────────────────────────────────
  const isSuperAdmin = user?.role === 'super_admin';
  const isAdminOrSuper = isSuperAdmin || user?.role === 'admin';

  useEffect(() => {
    if (isAdminOrSuper) bootstrap();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const bootstrap = async () => {
    try {
      setLoading(true);
      // Use allSettled so an isolated failure in one of the three calls
      // (e.g. /api/users 500'ing on a bad row) does NOT blank out the
      // entire Schedule tab — events should still render so the admin
      // can at least see and switch between scheduled events, even if
      // the repairer pool is briefly empty. Each failure surfaces its
      // own toast so the operator knows what's broken.
      const [schedSet, usersSet, settingsSet] = await Promise.allSettled([
        axios.get(`${API}/event-settings/schedule`, { headers: getAuthHeader() }),
        // Fetch ALL active users (not just role=volunteer) so Super Admins
        // and Admins who have opted in via `availability_role = "repairer"`
        // also appear in the drag-and-drop pool. The eligibility filter
        // below still keeps the pool strictly Repairers-only.
        axios.get(`${API}/users?active_only=true`, { headers: getAuthHeader() }),
        axios.get(`${API}/event-settings`, { headers: getAuthHeader() }),
      ]);

      // Events (most important — the switcher is the entry point)
      if (schedSet.status === 'fulfilled') {
        const today = new Date().toISOString().slice(0, 10);
        const futureFirst = (schedSet.value.data || []).slice()
          .sort((a, b) => a.sort_date.localeCompare(b.sort_date));
        const future = futureFirst.filter((e) => e.sort_date >= today);
        const display = future.length ? future : futureFirst;
        setEvents(display);
        const first = display[0]?.sort_date || '';
        setSelectedSortDate(first);
      } else {
        toast.error('Failed to load event schedule');
        setEvents([]);
      }

      // Repairer pool
      if (usersSet.status === 'fulfilled') {
        // Hard-blocked repairer emails (must match backend
        // roster_routes.BLOCKED_REPAIRER_EMAILS). These accounts exist
        // to operate the platform, not to repair, and are removed from
        // the pool regardless of `availability_role`.
        const BLOCKED = new Set(['adam@mushongasystems.com']);
        setRepairers(
          (usersSet.value.data || []).filter(
            (u) =>
              (u.availability_role || 'repairer') === 'repairer' &&
              !BLOCKED.has((u.email || '').toLowerCase())
          )
        );
      } else {
        toast.error('Failed to load repairer list');
        setRepairers([]);
      }

      // Time-slot configuration (cosmetic only — slot list still renders
      // as empty if this fails)
      if (settingsSet.status === 'fulfilled') {
        setTimeSlots(settingsSet.value.data?.time_slots || []);
      } else {
        setTimeSlots([]);
      }
    } finally {
      setLoading(false);
    }
  };

  // ─── Coverage loader ───────────────────────────────────────────────
  const loadCoverage = useCallback(async (sortDate) => {
    if (!sortDate) {
      setCoverage(null);
      setAtRisk([]);
      return;
    }
    try {
      const [covRes, riskRes] = await Promise.all([
        axios.get(
          `${API}/roster-assignments/coverage/${sortDate}`,
          { headers: getAuthHeader() },
        ),
        // At-risk is best-effort — never block coverage refresh on it.
        axios.get(
          `${API}/roster-assignments/at-risk/${sortDate}`,
          { headers: getAuthHeader() },
        ).catch(() => ({ data: { at_risk: [] } })),
      ]);
      setCoverage(covRes.data);
      setAtRisk(riskRes.data?.at_risk || []);
    } catch (err) {
      toast.error('Failed to load rota coverage');
      setCoverage(null);
      setAtRisk([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedSortDate) loadCoverage(selectedSortDate);
  }, [selectedSortDate, loadCoverage]);

  // ─── Derived ───────────────────────────────────────────────────────
  // Set of repairer ids already assigned (anywhere in this event) so the
  // pool can render them at lower opacity / disable double-drop hint.
  const assignedIds = useMemo(() => {
    if (!coverage?.slots) return new Set();
    const ids = new Set();
    coverage.slots.forEach((s) => s.assignments.forEach((a) => ids.add(a.user_id)));
    return ids;
  }, [coverage]);

  const selectedEvent = useMemo(
    () => events.find((e) => e.sort_date === selectedSortDate),
    [events, selectedSortDate],
  );

  // ─── Mutations ─────────────────────────────────────────────────────
  const assign = async (userId, slot) => {
    setBusy(true);
    try {
      await axios.post(
        `${API}/roster-assignments`,
        { event_sort_date: selectedSortDate, slot, user_id: userId },
        { headers: getAuthHeader() },
      );
      toast.success('Volunteer added to slot');
      loadCoverage(selectedSortDate);
    } catch (err) {
      const detail = err.response?.data?.detail || 'Failed to assign';
      toast.error(detail);
    } finally {
      setBusy(false);
    }
  };

  const move = async (assignmentId, newSlot) => {
    setBusy(true);
    try {
      await axios.patch(
        `${API}/roster-assignments/${assignmentId}/move`,
        { slot: newSlot },
        { headers: getAuthHeader() },
      );
      loadCoverage(selectedSortDate);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to move assignment');
    } finally {
      setBusy(false);
    }
  };

  const remove = async (assignmentId) => {
    setBusy(true);
    try {
      await axios.delete(
        `${API}/roster-assignments/${assignmentId}`,
        { headers: getAuthHeader() },
      );
      loadCoverage(selectedSortDate);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to remove');
    } finally {
      setBusy(false);
    }
  };

  // ─── Copy last event's rota ────────────────────────────────────────
  const copyLastRota = async () => {
    if (!selectedSortDate) return;
    const targetLabel = selectedEvent
      ? `${selectedEvent.event_date} (${selectedEvent.venue_name})`
      : selectedSortDate;
    const ok = window.confirm(
      `Copy the most recent previous event's rota into "${targetLabel}"?\n\n` +
        'Only active Repairer volunteers will be copied. Front of House, Unavailable, ' +
        'inactive, and deleted users are skipped. Existing assignments on this event ' +
        'are left untouched.',
    );
    if (!ok) return;
    setCopying(true);
    try {
      const res = await axios.post(
        `${API}/roster-assignments/copy-from-previous`,
        { target_event_sort_date: selectedSortDate },
        { headers: getAuthHeader() },
      );
      const { copied, skipped, source_event_date, reason } = res.data || {};
      if (reason === 'no_previous_event') {
        toast.info('No previous event with a rota was found.');
      } else if (copied === 0 && skipped === 0) {
        toast.info('Nothing to copy.');
      } else {
        const src = source_event_date ? ` from ${source_event_date}` : '';
        toast.success(
          `Copied ${copied} ${copied === 1 ? 'assignment' : 'assignments'}${src}` +
            (skipped ? ` · ${skipped} skipped` : ''),
        );
      }
      await loadCoverage(selectedSortDate);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to copy rota');
    } finally {
      setCopying(false);
    }
  };

  // ─── Print rota as PDF (admin + super_admin) ──────────────────────
  const printRotaPDF = async () => {
    if (!selectedSortDate) return;
    setPrinting(true);
    try {
      const resp = await axios.get(
        `${API}/roster-assignments/print/${selectedSortDate}`,
        { headers: getAuthHeader(), responseType: 'blob' },
      );
      const blob = new Blob([resp.data], { type: 'application/pdf' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `repair-cafe-rota-${selectedSortDate}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(a.href);
      toast.success('Rota PDF downloaded');
    } catch (err) {
      toast.error(
        err.response?.status === 403
          ? 'You do not have permission to print the rota'
          : 'Failed to generate PDF',
      );
    } finally {
      setPrinting(false);
    }
  };

  // ─── DnD handlers ──────────────────────────────────────────────────
  //
  // BUG FIX (Feb 2026, prod): Chrome shows a "not-allowed" stop-sign
  // cursor when the dragstart's `effectAllowed` doesn't include the
  // dragover's `dropEffect`. We were starting "user-from-pool" drags
  // with `effectAllowed='copy'` but every drop target requested
  // `dropEffect='move'` — incompatible, so Chrome silently rejected the
  // drop and the rota never updated.
  //
  // Fix: both drag sources start with `effectAllowed='move'`, and every
  // drop target also requests `dropEffect='move'`. Conceptually "adding
  // a repairer to a slot" is fine to model as a move (it's a one-shot
  // gesture; the same volunteer can still be assigned to multiple slots
  // because the source list re-renders from React state, not from the
  // DOM element being relocated).
  const onDragStartUser = (e, userId) => {
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', JSON.stringify({ kind: DRAG_KIND_USER, userId }));
  };
  const onDragStartAssignment = (e, assignmentId, fromSlot) => {
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData(
      'text/plain',
      JSON.stringify({ kind: DRAG_KIND_ASSIGNMENT, assignmentId, fromSlot }),
    );
  };
  const onDragOver = (e, slot) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (dragOverSlot !== slot) setDragOverSlot(slot);
  };
  const onDragLeave = () => setDragOverSlot(null);
  const onDrop = (e, slot) => {
    e.preventDefault();
    setDragOverSlot(null);
    let payload;
    try {
      payload = JSON.parse(e.dataTransfer.getData('text/plain') || '{}');
    } catch {
      return;
    }
    if (payload.kind === DRAG_KIND_USER && payload.userId) {
      assign(payload.userId, slot);
    } else if (payload.kind === DRAG_KIND_ASSIGNMENT && payload.assignmentId) {
      if (payload.fromSlot === slot) return; // no-op
      move(payload.assignmentId, slot);
    }
  };

  // ─── Render ────────────────────────────────────────────────────────
  if (!isAdminOrSuper) {
    return (
      <div className="p-8 text-center" data-testid="schedule-no-access">
        <p className="text-gray-600">Admin access required.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-8 text-center" data-testid="schedule-loading">
        <Loader2 className="w-6 h-6 animate-spin inline mr-2 text-[#E87B6A]" />
        Loading rota…
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto" data-testid="schedule-rota">
      <header className="mb-5 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold text-[#193460] flex items-center gap-2">
            <CalendarDays className="w-7 h-7" /> Schedule — Rota
          </h2>
          <p className="text-gray-600 mt-1 text-sm">
            Drag a repairer onto a time slot to schedule them. Capacity for the
            booking form is derived directly from this rota.
          </p>
        </div>
        {/* Event switcher + copy-last-rota */}
        <div className="flex flex-wrap items-center gap-2">
          <label htmlFor="event-switcher" className="text-sm text-gray-600 whitespace-nowrap">
            Event:
          </label>
          <select
            id="event-switcher"
            data-testid="schedule-event-switcher"
            value={selectedSortDate}
            onChange={(e) => setSelectedSortDate(e.target.value)}
            className="rounded-md border-2 border-[#193460] bg-white py-2 px-3 text-sm font-medium text-[#193460] focus:outline-none focus:ring-2 focus:ring-[#E87B6A]"
          >
            {events.length === 0 && <option value="">No events scheduled</option>}
            {events.map((ev) => (
              <option key={ev.sort_date} value={ev.sort_date}>
                {ev.event_date} · {ev.venue_name}
              </option>
            ))}
          </select>
          {isSuperAdmin && (
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={copying || !selectedSortDate}
              onClick={copyLastRota}
              data-testid="copy-last-rota-btn"
              className="border-[#193460] text-[#193460] hover:bg-[#193460] hover:text-white"
            >
              {copying ? (
                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
              ) : (
                <Copy className="w-4 h-4 mr-1" />
              )}
              Copy last event&rsquo;s rota
            </Button>
          )}
          <Button
            type="button"
            size="sm"
            disabled={printing || !selectedSortDate}
            onClick={printRotaPDF}
            data-testid="print-rota-pdf-btn"
            className="bg-[#193460] hover:bg-[#0f2240] text-white"
          >
            {printing ? (
              <Loader2 className="w-4 h-4 mr-1 animate-spin" />
            ) : (
              <Printer className="w-4 h-4 mr-1" />
            )}
            Print rota as PDF
          </Button>
        </div>
      </header>

      {!selectedEvent && (
        <Card className="border-2 border-dashed border-gray-300">
          <CardContent className="p-10 text-center text-gray-600">
            <CalendarDays className="w-10 h-10 mx-auto text-gray-400 mb-3" />
            <p className="font-medium">No event selected.</p>
            <p className="text-sm mt-1">
              Add an event on the <strong>Event Settings → Upcoming Events</strong> section to
              start building a rota.
            </p>
          </CardContent>
        </Card>
      )}

      {selectedEvent && (
        <>
          {/* Coverage banner */}
          {coverage?.needs_roster && (
            <div
              className="mb-4 p-4 rounded-lg border-2 border-amber-300 bg-amber-50 flex items-start gap-3"
              data-testid="schedule-needs-roster-banner"
            >
              <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-amber-900">
                <p className="font-semibold">No repairers scheduled yet.</p>
                <p>
                  Booking is closed for this event until you place at least one
                  Repairer on the rota. Drag from the volunteer list to a time
                  slot below.
                </p>
              </div>
            </div>
          )}

          {coverage && !coverage.needs_roster && coverage.empty_slots?.length > 0 && (
            <div
              className="mb-4 p-3 rounded-lg border border-amber-200 bg-amber-50/60 text-sm text-amber-900 flex items-start gap-2"
              data-testid="schedule-empty-slots-warning"
            >
              <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>
                <strong>{coverage.empty_slots.length}</strong> slot
                {coverage.empty_slots.length === 1 ? ' is' : 's are'} still empty:{' '}
                <span className="font-medium">{coverage.empty_slots.join(', ')}</span>. Those
                slots will be closed on the booking form until a repairer is added.
              </span>
            </div>
          )}

          {/* At-risk bookings panel — flags any slot/category where
              active bookings now exceed the rota capacity after a rota
              change. Warning only, never blocks the rota edit. */}
          {atRisk.length > 0 && (
            <div
              className="mb-4 p-4 rounded-lg border-2 border-red-300 bg-red-50"
              role="alert"
              data-testid="schedule-at-risk-panel"
            >
              <div className="flex items-start gap-3 mb-2">
                <ShieldAlert className="w-5 h-5 text-red-700 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-red-900">
                    At-risk bookings ({atRisk.length})
                  </p>
                  <p className="text-sm text-red-800 mt-0.5">
                    Existing bookings now exceed the rota capacity after
                    your latest change. Re-assign covering repairers or
                    contact the affected customers before event day.
                  </p>
                </div>
              </div>
              <ul className="mt-3 space-y-1.5">
                {atRisk.map((r, i) => (
                  <li
                    key={`${r.slot}-${r.category_id || 'slot'}-${i}`}
                    data-testid={`schedule-at-risk-row-${i}`}
                    className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-sm text-red-900 pl-1"
                  >
                    <span className="font-mono font-semibold">{r.slot}</span>
                    <span className="text-red-400">|</span>
                    <span className="font-medium inline-flex items-center gap-1">
                      {r.kind === 'slot' && (
                        <span
                          className="px-1.5 py-0.5 rounded bg-red-200 text-[10px] uppercase tracking-wider font-bold"
                          data-testid={`schedule-at-risk-kind-${i}`}
                        >
                          Slot total
                        </span>
                      )}
                      {r.category_name}
                    </span>
                    <span className="text-red-400">|</span>
                    <span>
                      <strong>{r.active_bookings}</strong> booking
                      {r.active_bookings === 1 ? '' : 's'} /{' '}
                      <strong>{r.capacity}</strong> repairer
                      {r.capacity === 1 ? '' : 's'}
                    </span>
                    <span className="text-red-400">|</span>
                    <span
                      className="inline-flex items-center px-2 py-0.5 rounded-full bg-red-200 text-red-900 text-xs font-semibold"
                      data-testid={`schedule-at-risk-over-${i}`}
                    >
                      {r.over_by} over capacity
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className={isSuperAdmin ? "grid lg:grid-cols-[260px_1fr] gap-5" : "grid gap-5"}>
            {/* Repairer pool — super-admin only (drag source) */}
            {isSuperAdmin && (
            <aside
              className="bg-white border-2 border-gray-200 rounded-lg p-3 self-start"
              data-testid="schedule-repairer-pool"
            >
              <div className="flex items-center gap-2 mb-3 px-1">
                <Users className="w-4 h-4 text-[#193460]" />
                <h3 className="text-sm font-semibold text-[#193460]">
                  Active Repairers ({repairers.length})
                </h3>
              </div>
              {repairers.length === 0 ? (
                <p className="text-sm text-gray-500 italic px-1">
                  No active repairers. Add a volunteer with Booking Role =
                  &quot;Repairer&quot; on the Users tab.
                </p>
              ) : (
                <ul className="space-y-2">
                  {repairers.map((r) => {
                    const used = assignedIds.has(r.id);
                    return (
                      <li
                        key={r.id}
                        draggable
                        onDragStart={(e) => onDragStartUser(e, r.id)}
                        data-testid={`repairer-card-${r.id}`}
                        className={`flex items-center gap-2 px-3 py-2 rounded-md border bg-white cursor-grab active:cursor-grabbing hover:shadow-sm hover:border-[#E87B6A] transition ${
                          used ? 'opacity-60' : ''
                        }`}
                      >
                        <GripVertical className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-[#193460] truncate">
                            {r.name}
                          </p>
                          <p className="text-[11px] text-gray-500 truncate">
                            {r.email}
                          </p>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
              <p className="text-[11px] text-gray-500 mt-3 px-1">
                Tip: a repairer can be dropped into multiple slots in the same event.
              </p>
            </aside>
            )}

            {/* Slots grid */}
            <section className="space-y-3" data-testid="schedule-slots-grid">
              {timeSlots.length === 0 && (
                <Card className="border-2 border-dashed border-gray-300">
                  <CardContent className="p-8 text-center text-gray-600">
                    <p className="font-medium">No time slots configured.</p>
                    <p className="text-sm mt-1">
                      Add slots on the <strong>Event Settings</strong> tab first.
                    </p>
                  </CardContent>
                </Card>
              )}

              {timeSlots.map((slot) => {
                const slotData = coverage?.slots?.find((s) => s.slot === slot);
                const assignments = slotData?.assignments || [];
                const isEmpty = assignments.length === 0;
                const isHover = dragOverSlot === slot;
                return (
                  <div
                    key={slot}
                    onDragOver={isSuperAdmin ? (e) => onDragOver(e, slot) : undefined}
                    onDragLeave={isSuperAdmin ? onDragLeave : undefined}
                    onDrop={isSuperAdmin ? (e) => onDrop(e, slot) : undefined}
                    data-testid={`schedule-slot-${slot.replace(/[^a-z0-9]/gi, '-')}`}
                    className={`p-3 rounded-lg border-2 transition ${
                      isHover
                        ? 'border-[#E87B6A] bg-orange-50/60'
                        : isEmpty
                        ? 'border-dashed border-amber-300 bg-amber-50/30'
                        : 'border-gray-200 bg-white'
                    }`}
                  >
                    <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
                      <div className="flex items-center gap-2">
                        <h4 className="font-semibold text-[#193460]">{slot}</h4>
                        <Badge
                          variant="outline"
                          className={
                            isEmpty
                              ? 'border-amber-400 text-amber-700 bg-amber-50'
                              : 'border-green-500 text-green-700 bg-green-50'
                          }
                        >
                          {assignments.length} {assignments.length === 1 ? 'repairer' : 'repairers'}
                        </Badge>
                      </div>
                      {isEmpty && isSuperAdmin && (
                        <span className="text-xs text-amber-800 italic">
                          Drop a repairer here to open this slot for booking
                        </span>
                      )}
                    </div>

                    {/* Chips */}
                    <div className="flex flex-wrap gap-2 min-h-[36px]">
                      {assignments.map((a) => (
                        <div
                          key={a.id}
                          draggable={isSuperAdmin}
                          onDragStart={isSuperAdmin ? (e) => onDragStartAssignment(e, a.id, slot) : undefined}
                          data-testid={`assignment-chip-${a.id}`}
                          className={`group inline-flex items-center gap-1 pl-2 pr-1 py-1 rounded-full bg-[#193460] text-white text-sm ${
                            isSuperAdmin ? 'cursor-grab active:cursor-grabbing' : ''
                          }`}
                          title={isSuperAdmin
                            ? 'Assigned by drag-and-drop. Drag to move, or click ✕ to remove.'
                            : 'Read-only — Super Admins can edit assignments.'}
                        >
                          {isSuperAdmin && <GripVertical className="w-3 h-3 text-white/70" />}
                          <span className="truncate max-w-[140px]">{a.volunteer_name}</span>
                          {isSuperAdmin && (
                            <button
                              type="button"
                              disabled={busy}
                              onClick={() => remove(a.id)}
                              aria-label={`Remove ${a.volunteer_name} from ${slot}`}
                              data-testid={`assignment-remove-${a.id}`}
                              className="ml-1 w-5 h-5 inline-flex items-center justify-center rounded-full bg-white/20 hover:bg-red-500 transition"
                            >
                              <UserMinus className="w-3 h-3" />
                            </button>
                          )}
                        </div>
                      ))}
                      {isEmpty && (
                        <span className="text-xs text-gray-400 italic">— empty —</span>
                      )}
                    </div>
                  </div>
                );
              })}

              {/* Summary footer */}
              {coverage && timeSlots.length > 0 && (
                <div
                  className="text-xs text-gray-600 px-1 pt-2 flex flex-wrap gap-x-4 gap-y-1"
                  data-testid="schedule-summary"
                >
                  <span>
                    Total assignments:{' '}
                    <strong className="text-[#193460]">{coverage.total_assignments}</strong>
                  </span>
                  <span>
                    Empty slots:{' '}
                    <strong className={coverage.empty_slots.length ? 'text-amber-700' : 'text-green-700'}>
                      {coverage.empty_slots.length}
                    </strong>{' '}
                    / {timeSlots.length}
                  </span>
                </div>
              )}
            </section>
          </div>
        </>
      )}
    </div>
  );
}
